(function () {
  const configElement = document.getElementById('ElyseeGeoLanguageDetectorConfig');

  if (!configElement) return;

  let config = {};

  try {
    config = JSON.parse(configElement.textContent || '{}');
  } catch (error) {
    return;
  }

  const availableLanguages = Array.isArray(config.availableLanguages)
    ? config.availableLanguages.filter(Boolean)
    : [];
  const currentLanguage = normalizeLanguage(config.currentLanguage);
  const currentCountry = normalizeCountry(config.currentCountry);
  const manualStorageKey = 'elysee:language-preference';
  const checkedStorageKey = 'elysee:geo-language-detector:checked';
  const explicitStorageKey = 'elysee:geo-language-detector:explicit';
  const preferenceMaxAge = 180 * 24 * 60 * 60 * 1000;
  const germanSpeakingCountries = ['DE', 'AT', 'CH', 'LI'];

  if (!currentLanguage || availableLanguages.length < 2) return;
  if (isAuditOrCrawler()) return;

  const availableLanguageMap = availableLanguages.reduce((map, language) => {
    const normalized = normalizeLanguage(language);

    if (normalized) {
      map.set(normalized, language);
      map.set(normalized.split('-')[0], language);
    }

    return map;
  }, new Map());

  bindManualLanguageChoice();

  if (hasManualPreference() || hasCheckedCurrentSession()) return;

  if (isExplicitLanguagePath(window.location.pathname)) {
    rememberManualPreference(config.currentLanguage || currentLanguage);
    markCheckedCurrentSession();
    return;
  }

  scheduleDetection();

  function scheduleDetection() {
    const run = () => {
      detectLanguage()
        .then((targetLanguage) => {
          if (!targetLanguage || normalizeLanguage(targetLanguage) === currentLanguage) {
            markCheckedCurrentSession();
            return;
          }

          submitLanguage(targetLanguage);
        })
        .catch(() => {
          markCheckedCurrentSession();
        });
    };

    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(run, { timeout: 2500 });
      return;
    }

    window.setTimeout(run, 900);
  }

  async function detectLanguage() {
    const suggestion = await getShopifySuggestion();
    const suggestionLanguage = getLanguageFromSuggestion(suggestion);

    if (suggestionLanguage) return suggestionLanguage;

    const suggestionCountry = getCountryFromSuggestion(suggestion) || currentCountry;
    const countryLanguage = getLanguageFromCountry(suggestionCountry);

    if (countryLanguage) return countryLanguage;

    return getLanguageFromBrowser();
  }

  async function getShopifySuggestion() {
    if (!window.fetch) return null;

    const url = new URL('/browsing_context_suggestions.json', window.location.origin);

    url.searchParams.set('country[enabled]', 'true');
    url.searchParams.set('language[enabled]', 'true');

    if (currentCountry) url.searchParams.set('country[exclude]', currentCountry);
    if (currentLanguage) url.searchParams.set('language[exclude]', currentLanguage);

    const response = await window.fetch(url.toString(), {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });

    if (!response.ok) return null;

    return response.json();
  }

  function getLanguageFromSuggestion(suggestion) {
    const languageCandidates = [];

    collectValuesForKeys(suggestion, ['language', 'locale'], languageCandidates);

    for (const candidate of languageCandidates) {
      const language = getCodeFromValue(candidate);
      const available = getAvailableLanguage(language);

      if (available) return available;
    }

    return null;
  }

  function getCountryFromSuggestion(suggestion) {
    const countryCandidates = [];

    collectValuesForKeys(suggestion, ['country'], countryCandidates);

    for (const candidate of countryCandidates) {
      const country = normalizeCountry(getCodeFromValue(candidate));

      if (country) return country;
    }

    return null;
  }

  function collectValuesForKeys(value, keys, results) {
    if (!value || typeof value !== 'object') return;

    if (Array.isArray(value)) {
      value.forEach((item) => collectValuesForKeys(item, keys, results));
      return;
    }

    Object.entries(value).forEach(([key, nestedValue]) => {
      if (keys.includes(key)) results.push(nestedValue);
      collectValuesForKeys(nestedValue, keys, results);
    });
  }

  function getCodeFromValue(value) {
    if (!value) return '';
    if (typeof value === 'string') return value;

    if (typeof value === 'object') {
      return value.iso_code || value.isoCode || value.handle || value.code || value.locale || '';
    }

    return '';
  }

  function getLanguageFromCountry(country) {
    if (!country) return null;

    if (germanSpeakingCountries.includes(country)) {
      return getAvailableLanguage('de');
    }

    return getAvailableLanguage('en');
  }

  function getLanguageFromBrowser() {
    const languages = Array.isArray(window.navigator.languages) && window.navigator.languages.length
      ? window.navigator.languages
      : [window.navigator.language];

    for (const language of languages) {
      const available = getAvailableLanguage(language);

      if (available) return available;
    }

    return getAvailableLanguage('en') || getAvailableLanguage('de');
  }

  function getAvailableLanguage(language) {
    const normalized = normalizeLanguage(language);

    if (!normalized) return null;

    return availableLanguageMap.get(normalized) || availableLanguageMap.get(normalized.split('-')[0]) || null;
  }

  function submitLanguage(language) {
    const targetLanguage = getAvailableLanguage(language);

    if (!targetLanguage || normalizeLanguage(targetLanguage) === currentLanguage) {
      markCheckedCurrentSession();
      return;
    }

    markCheckedCurrentSession();
    rememberAutoAttempt(targetLanguage);

    const form = findLocalizationForm() || createLocalizationForm();
    const returnTo = window.location.pathname + window.location.search + window.location.hash;

    setFormValue(form, 'language_code', targetLanguage);
    setFormValue(form, 'return_to', returnTo);

    form.setAttribute('data-elysee-geo-detector-form', 'true');
    form.submit();
  }

  function findLocalizationForm() {
    return document.querySelector('form[action*="/localization"], form input[name="form_type"][value="localization"]')?.closest('form') || null;
  }

  function createLocalizationForm() {
    const rootUrl = typeof config.rootUrl === 'string' && config.rootUrl ? config.rootUrl : '/';
    const form = document.createElement('form');

    form.method = 'post';
    form.action = joinPaths(rootUrl, 'localization');
    form.acceptCharset = 'UTF-8';
    form.hidden = true;

    setFormValue(form, 'form_type', 'localization');
    setFormValue(form, 'utf8', '\u2713');
    setFormValue(form, '_method', 'put');

    document.body.appendChild(form);

    return form;
  }

  function setFormValue(form, name, value) {
    let input = form.querySelector(`input[name="${name}"]`);

    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      form.appendChild(input);
    }

    input.value = value;
  }

  function bindManualLanguageChoice() {
    document.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-elysee-language-choice], button[name="language_code"], input[name="language_code"]');

      if (!trigger) return;

      const language = trigger.getAttribute('data-elysee-language-choice') || trigger.value;

      rememberManualPreference(language);
    });

    document.addEventListener('change', (event) => {
      const target = event.target;

      if (!target || target.name !== 'language_code') return;

      rememberManualPreference(target.value);
    });

    document.addEventListener('submit', (event) => {
      const form = event.target;

      if (!form || form.getAttribute('data-elysee-geo-detector-form') === 'true') return;
      if (!form.matches('form[action*="/localization"]') && !form.querySelector('input[name="form_type"][value="localization"]')) return;

      const languageInput = form.querySelector('[name="language_code"]');

      if (languageInput && languageInput.value) {
        rememberManualPreference(languageInput.value);
      }
    });
  }

  function rememberManualPreference(language) {
    const available = getAvailableLanguage(language);

    if (!available) return;

    try {
      window.localStorage.setItem(
        manualStorageKey,
        JSON.stringify({ language: available, expires: Date.now() + preferenceMaxAge })
      );
    } catch (error) {
      return;
    }
  }

  function hasManualPreference() {
    try {
      const value = JSON.parse(window.localStorage.getItem(manualStorageKey) || 'null');

      if (!value || !value.expires || value.expires < Date.now()) {
        window.localStorage.removeItem(manualStorageKey);
        return false;
      }

      return Boolean(getAvailableLanguage(value.language));
    } catch (error) {
      return false;
    }
  }

  function rememberAutoAttempt(language) {
    try {
      window.sessionStorage.setItem(explicitStorageKey, language);
    } catch (error) {
      return;
    }
  }

  function hasCheckedCurrentSession() {
    try {
      return window.sessionStorage.getItem(checkedStorageKey) === 'true';
    } catch (error) {
      return false;
    }
  }

  function markCheckedCurrentSession() {
    try {
      window.sessionStorage.setItem(checkedStorageKey, 'true');
    } catch (error) {
      return;
    }
  }

  function isExplicitLanguagePath(pathname) {
    const segments = pathname.split('/').filter(Boolean);

    if (!segments.length) return false;

    return Boolean(getAvailableLanguage(segments[0]));
  }

  function isAuditOrCrawler() {
    const userAgent = String(window.navigator.userAgent || '');
    const auditOrCrawlerPattern =
      /Chrome-Lighthouse|Lighthouse|PageSpeed|Google Page Speed|HeadlessChrome|Googlebot|Google-InspectionTool|bot|crawler|spider/i;

    return auditOrCrawlerPattern.test(userAgent);
  }

  function normalizeLanguage(language) {
    return String(language || '').trim().toLowerCase().replace('_', '-');
  }

  function normalizeCountry(country) {
    return String(country || '').trim().toUpperCase();
  }

  function joinPaths(root, path) {
    return `${root.replace(/\/+$/, '')}/${path.replace(/^\/+/, '')}`;
  }
})();
